import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "react-bootstrap";
import ESTooltip from "../../controls/ESTooltip";

const CustomizedPlan = () => {
  const [totalAmount, setTotalAmount] = useState(0);
  const [isSentimentalAnalysis, setIsSentimentalAnalysis] = useState(false);
  const [isResponsibleAnalysis, setIsResponsibleAnalysis] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      surveyCount: null,
      userCount: null,
      questionCount: null,
    },
  });

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      isSentimentalAnalysis,
      isResponsibleAnalysis,
    };
   
  };

  const watchedValues = watch();

  useEffect(() => {
    const surveyPrice = (watchedValues?.surveyCount ?? 0) * 20;
    const userPrice = (watchedValues?.userCount ?? 0) * 10;
    const questionPrice = (watchedValues?.questionCount ?? 0) * 15;
    const sentimentalPrice = isSentimentalAnalysis ? 100 : 0;
    const responsiblePrice = isResponsibleAnalysis ? 100 : 0;

    const total =
      surveyPrice +
      userPrice +
      questionPrice +
      sentimentalPrice +
      responsiblePrice;

    setTotalAmount(total);
  }, [isSentimentalAnalysis, isResponsibleAnalysis, watchedValues]);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg"
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-center text-gray-800">
            Customize Your Plan
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex flex-col d-flex justify-content-center p-4 bg-gray-50 rounded-lg">
            <label className="text-gray-700 font-medium">
              Number of Surveys
            </label>

            <ESTooltip
              text="Specify the total number of surveys you plan to conduct. Each survey can include multiple questions and participants. The minimum allowed is 1, and the maximum is 2000."
              placement="right"
            >
              <button
                type="button"
                className="ml-3 text-blue-500 cursor-pointer bg-transparent border-none p-0 text-lg font-semibold focus:outline-none"
                aria-label="Information"
              >
                i
              </button>
            </ESTooltip>

            <input
              type="number"
              {...register("surveyCount", {
                required: "Number of surveys is required",
                min: {
                  value: 1,
                  message: "Minimum value is 1",
                },
                max: {
                  value: 2000,
                  message: "Minimum value is 2000",
                },
              })}
              className={`px-4 py-2 w-full rounded-md border ${
                errors.surveyCount ? "border-red-500" : "border-gray-300"
              } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none`}
            />
            {errors.surveyCount && (
              <div className="text-danger text-sm mt-2">
                {errors.surveyCount.message}
              </div>
            )}
          </div>

          <div className="flex flex-col d-flex justify-content-center p-4 bg-gray-50 rounded-lg">
            <label className="text-gray-700 font-medium">Number of Users</label>

            <ESTooltip
              text="You can add only the number of users specified in this field. Minimum users allowed is 1, and the maximum is 2000."
              placement="right"
            >
              <button
                type="button"
                className="ml-2 text-blue-500 cursor-pointer bg-transparent border-none p-0 text-lg font-semibold focus:outline-none"
                aria-label="Information"
              >
                i
              </button>
            </ESTooltip>

            <input
              type="number"
              {...register("userCount", {
                required: "Number of users is required",
                min: {
                  value: 1,
                  message: "Minimum value is 1",
                },
                max: {
                  value: 2000,
                  message: "Minimum value is 2000",
                },
              })}
              className={`px-4 py-2 w-full rounded-md border ${
                errors.userCount ? "border-danger" : "border-gray-300"
              } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none`}
            />
            {errors.userCount && (
              <div className="text-danger text-sm mt-2 mr-5">
                {errors.userCount.message}
              </div>
            )}
          </div>

          <div className="flex flex-col p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center d-flex justify-content-center mb-2">
              <label className="text-gray-700 font-medium">
                Questions per Survey
              </label>

              <ESTooltip
                text="Specify the total number of questions to include in each survey. Ensure the number aligns with your survey goals. The minimum allowed is 1, and the maximum is 2000."
                placement="right"
              >
                <button
                  type="button"
                  className="ml-2 text-blue-500 cursor-pointer bg-transparent border-none p-0 text-lg font-semibold focus:outline-none"
                  aria-label="Information"
                >
                  i
                </button>
              </ESTooltip>

              <input
                type="number"
                {...register("questionCount", {
                  required: "Number of questions is required",
                  min: {
                    value: 1,
                    message: "Minimum value is 1",
                  },
                  max: {
                    value: 2000,
                    message: "Minimum value is 2000",
                  },
                })}
                className={`px-4 py-2 w-full rounded-md border ${
                  errors.questionCount ? "border-red-500" : "border-gray-300"
                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none`}
              />
              {errors.questionCount && (
                <div className="text-danger text-sm mt-2">
                  {errors.questionCount.message}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center d-flex justify-content-center justify-between">
              <div>
                <h4 className="font-medium text-gray-700">
                  Sentimental Analysis
                </h4>
                <p className="text-sm text-gray-500">
                  <input
                    type="checkbox"
                    checked={isSentimentalAnalysis}
                    onChange={(e) => setIsSentimentalAnalysis(e.target.checked)}
                    className="sr-only peer"
                  />{" "}
                  Add sentiment analysis to your surveys
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center d-flex justify-content-center justify-between">
              <div>
                <h4 className="font-medium text-gray-700">
                  Responsible Analysis Report
                </h4>
                <p className="text-sm text-gray-500">
                  <input
                    type="checkbox"
                    checked={isResponsibleAnalysis}
                    onChange={(e) => setIsResponsibleAnalysis(e.target.checked)}
                    className="sr-only peer"
                  />{" "}
                  Get detailed analysis reports
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          <div className="mt-8 p-6 bg-blue-50 rounded-lg">
            <div className="flex justify-between d-flex justify-content-center items-center">
              <p className="col-md-3 text-danger">
                Note: The validity period is 1 year from subscription. Sentiment
                analysis will be performed one time in batch mode.
              </p>

              <Button type="submit" className="btn btn-success ">
                Buy Now
              </Button>
              <br />
              <hr className="my-2 border-gray-500" />

              <h1 className="text-lg d-flex justify-content-center font-semibold text-gray-700">
                Total Amount: ₹{totalAmount}
              </h1>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CustomizedPlan;
